import { useContext, useEffect, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { EmailSettings } from "@/api/users";
import { useTranslation } from "react-i18next";
import { AuthContext, AuthContextType } from "@/context/AuthContext";
import { caseReviewAuthApi } from "@/api/auth";

function UserEmailSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { values: contextValues } = useContext(AuthContext) as AuthContextType;
  const { user } = contextValues;
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSettings = async () => {
    try {
      const response = await caseReviewAuthApi.getEmailSettings({
        token: user?.access || "",
      });
      setSettings(response.data);
    } catch (error) {
      toast({
        title: t("common.title.error"),
        description: t("users.toast.email_settings_load_failed"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (field: keyof EmailSettings) => {
    if (!settings) return;

    try {
      const newSettings = {
        ...settings,
        [field]: !settings[field],
      };
      const response = await caseReviewAuthApi.updateEmailSettings({
        data: newSettings,
        token: user?.access || "",
      });
      setSettings(response.data);
      toast({
        title: t("users.toast.email_settings_updated"),
      });
    } catch (error) {
      toast({
        title: t("users.toast.email_settings_update_failed"),
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div>{t("common.button.loading")}</div>;
  }

  if (!settings) {
    return <div>{t("users.statictext.email_settings_error")}</div>;
  }

  return (
    <div className="container mx-auto py-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("users.label.email_settings")}</CardTitle>
          <CardDescription>
            {t("users.statictext.email_settings_description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="ocr-notifications">
                {t("users.label.ocr_notifications")}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t("users.statictext.ocr_notifications_description")}
              </p>
            </div>
            <Switch
              id="ocr-notifications"
              checked={settings.receive_document_ocr_notifications}
              onCheckedChange={() =>
                handleToggle("receive_document_ocr_notifications")
              }
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default UserEmailSettings;
